import React, { useState, useEffect } from "react";

interface DonutDataItem {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data: DonutDataItem[];
  totalLabel?: string;
  size?: number;
  thickness?: number;
}

export const DonutChart: React.FC<DonutChartProps> = ({ 
  data, 
  totalLabel = "Total", 
  size = 140, 
  thickness = 25 
}) => {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    // Small delay to trigger draw-in animation
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const total = data.reduce((acc, curr) => acc + curr.value, 0);
  
  if (total === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", border: `${thickness}px solid var(--card-border)`, color: "var(--color-text-subtle)", fontSize: "11px", textAlign: "center", padding: "10px" }}>
        No attacks recorded in this period
      </div>
    );
  }

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let currentOffset = 0;
  
  // Sort data largest first for better look, unless "Others" is last
  const sortedData = [...data];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg 
          width={size} 
          height={size} 
          viewBox={`0 0 ${size} ${size}`} 
          style={{ transform: "rotate(-90deg)", overflow: "visible" }}
        >
          {sortedData.map((item, i) => {
            const percentage = item.value / total;
            const strokeDasharray = `${circumference} ${circumference}`;
            const targetDashoffset = circumference - (percentage * circumference);
            const initialDashoffset = circumference;
            const strokeDashoffset = mounted ? targetDashoffset : initialDashoffset;
            const offset = currentOffset;
            
            // Adjust offset for gap
            const gap = 2; // px
            const gapOffset = (gap / circumference) * circumference;
            
            currentOffset -= (percentage * circumference);

            const isHovered = activeIdx === i;
            const strokeWidth = isHovered ? thickness + 4 : thickness;

            return (
              <circle
                key={item.label}
                cx={center}
                cy={center}
                r={radius}
                fill="transparent"
                stroke={item.color}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="butt"
                style={{
                  transition: "stroke-dashoffset 1s ease-out, stroke-width 0.2s ease, opacity 0.2s ease",
                  transformOrigin: "center",
                  transform: `rotate(${(offset / circumference) * 360}deg)`,
                  opacity: activeIdx === null || isHovered ? 1 : 0.4,
                  cursor: "pointer"
                }}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseLeave={() => setActiveIdx(null)}
                tabIndex={0}
                onFocus={() => setActiveIdx(i)}
                onBlur={() => setActiveIdx(null)}
                aria-label={`${item.label}: ${item.value} (${Math.round(percentage * 100)}%)`}
              />
            );
          })}
        </svg>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--color-text)", lineHeight: 1 }}>{total}</div>
          <div style={{ fontSize: "10px", color: "var(--color-text-subtle)", textTransform: "uppercase", marginTop: "2px" }}>{totalLabel}</div>
        </div>
        
        {/* Tooltip */}
        {activeIdx !== null && (
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 10, background: "var(--color-surface-2)", border: "1px solid var(--card-border)", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", color: "var(--color-text)", pointerEvents: "none", whiteSpace: "nowrap", opacity: 0 }}>
             {sortedData[activeIdx].label}: {sortedData[activeIdx].value}
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
        {sortedData.map((item, i) => {
          const percentage = Math.round((item.value / total) * 100);
          const isHovered = activeIdx === i;
          return (
            <div 
              key={item.label} 
              style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", opacity: activeIdx === null || isHovered ? 1 : 0.4, transition: "opacity 0.2s" }}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseLeave={() => setActiveIdx(null)}
            >
              <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: item.color }} />
              <div style={{ fontSize: "11px", color: "var(--color-text)", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.label}>
                {item.label}
              </div>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-text)", width: "32px", textAlign: "right" }}>
                {percentage}%
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Hidden data table for screen readers */}
      <table style={{ display: "none" }}>
        <caption>Data for donut chart</caption>
        <tbody>
          {sortedData.map(item => (
            <tr key={item.label}>
              <th scope="row">{item.label}</th>
              <td>{item.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
